import { useEffect, useId, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { NotFoundState } from "@/components/ui/empty-state";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { decryptIdFromUrl } from "@/utils/urlCrypto";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  User,
  Users,
  MessageSquare,
  FileImage,
  History,
  CheckCircle,
  XCircle,
  Send,
  UserPlus,
  AlertTriangle,
  Eye,
  FileText,
  Loader2,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SolicitacaoStatusBadge } from "@/components/solicitacoes/SolicitacaoStatusBadge";
import { SolicitacaoPrioridadeBadge } from "@/components/solicitacoes/SolicitacaoPrioridadeBadge";
import { SLABadge } from "@/components/solicitacoes/SLABadge";
import { ValidacaoBadge } from "@/components/solicitacoes/ValidacaoBadge";
import { useToast } from "@/hooks/use-toast";
import { resolveMediaUrl, useImageBlobUrl } from "@/lib/media";
import { useSolicitacoesStore } from "@/stores/solicitacoesStore";
import { useAgentePermissions } from "@/hooks/useAgentePermissions";
import { useAgentesStore } from "@/stores/agentesStore";
import {
  TIPO_LABELS,
  STATUS_LABELS,
  calcularTempoDecorrido,
  calcularTempoRestante,
  calcularStatusSLA,
  type StatusSolicitacao,
  type Solicitacao,
} from "@/types/solicitacao";

function AgentAvatar({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const avatarUrl = useImageBlobUrl(resolveMediaUrl(src) || src);
  return (
    <Avatar className={className}>
      {avatarUrl ? <AvatarImage src={avatarUrl} alt={alt} /> : null}
      <AvatarFallback className="bg-primary/20 text-primary font-semibold text-sm">
        {alt.slice(0, 2).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

const GradientFavoriteIcon = ({
  className = "h-5 w-5",
}: {
  className?: string;
}) => {
  const gradientId = useId();
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(var(--primary))" />
          <stop offset="100%" stopColor="hsl(var(--secondary))" />
        </linearGradient>
      </defs>
      <path fill="none" d="M0 0h24v24H0V0z" />
      <path
        fill={`url(#${gradientId})`}
        stroke={`url(#${gradientId})`}
        strokeWidth="1.25"
        strokeLinejoin="round"
        d="M20.2246094,4.5693359C17.9908447,2.3260498,14.4058838,2.1826172,12,4.2402344C9.5941162,2.1826172,6.0091553,2.3260498,3.7753906,4.5693359c-2.3632812,2.3798218-2.3632812,6.2207642,0,8.6005859l7.8701172,7.8955078c0.09375,0.0944824,0.2213745,0.147583,0.3544922,0.1474609c0.1331177,0.0001221,0.2607422-0.0529785,0.3544922-0.1474609l7.8701172-7.8955078C22.5878906,10.7901001,22.5878906,6.9491577,20.2246094,4.5693359z"
      />
    </svg>
  );
};

function AnexoThumbnail({
  url,
  alt,
  className,
}: {
  url: string;
  alt: string;
  className?: string;
}) {
  const blobUrl = useImageBlobUrl(resolveMediaUrl(url) || url);
  // if (!blobUrl) {
  //   return (
  //     <div className={`w-full h-full flex items-center justify-center bg-muted text-muted-foreground text-xs ${className || ""}`}>
  //       Carregando...
  //     </div>
  //   );
  // }
  return (
    <img
      src={blobUrl}
      alt={alt}
      className={className}
      loading="lazy"
      onError={(e) => {
        const target = e.currentTarget as HTMLImageElement;
        target.style.display = "none";
      }}
    />
  );
}

export default function SolicitacaoDetalhesPage() {
  const { id: encryptedId } = useParams<{ id: string }>();
  const [decryptedId, setDecryptedId] = useState<string>("");
  const navigate = useNavigate();
  const [novoComentario, setNovoComentario] = useState("");
  const [observacaoInterna, setObservacaoInterna] = useState("");
  const [novoStatus, setNovoStatus] = useState<StatusSolicitacao | "">("");
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showAtribuirAgenteDialog, setShowAtribuirAgenteDialog] = useState(false);
  const [showEncaminharDialog, setShowEncaminharDialog] = useState(false);
  const [agenteSelecionado, setAgenteSelecionado] = useState("");
  const [ouvidoriaSelecionada, setOuvidoriaSelecionada] = useState("");
  const { toast } = useToast();
  const { isReadOnly } = useAgentePermissions();
  const { agentes, fetchAgentes } = useAgentesStore();

  // Decrypt ID from URL
  useEffect(() => {
    const decryptId = async () => {
      if (encryptedId) {
        const decrypted = await decryptIdFromUrl(encryptedId);
        setDecryptedId(decrypted);
      }
    };
    decryptId();
  }, [encryptedId]);

  const {
    solicitacaoAtual,
    isLoading,
    isSaving,
    error,
    fetchSolicitacaoById,
    atualizarStatus,
    aprovarSolicitacao,
    reprovarSolicitacao,
    adicionarComentario,
    adicionarObservacaoInterna,
    refreshSolicitacoes,
    atribuirAgente,
    transferirSolicitacao,
    ouvidoriasSupervisionadas,
    fetchOuvidoriasSupervisionadas,
  } = useSolicitacoesStore();

  useEffect(() => {
    if (decryptedId) {
      fetchSolicitacaoById(decryptedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decryptedId]);

  // Sempre que a página ganhar foco ou voltar à aba, recarrega os dados
  useEffect(() => {
    const handleFocus = () => {
      if (decryptedId) {
        fetchSolicitacaoById(decryptedId);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && decryptedId) {
        fetchSolicitacaoById(decryptedId);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decryptedId]);

  useEffect(() => {
    // Fetch approved agents when dialog opens
    if (showAtribuirAgenteDialog) {
      fetchAgentes("Aprovado");
    }
  }, [showAtribuirAgenteDialog, fetchAgentes]);

  useEffect(() => {
    // Fetch supervised ouvidorias when dialog opens
    if (showEncaminharDialog) {
      fetchOuvidoriasSupervisionadas();
    }
  }, [showEncaminharDialog, fetchOuvidoriasSupervisionadas]);

  // Early return if no ID
  if (!decryptedId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", {
        locale: ptBR,
      });
    } catch {
      return dateString;
    }
  };

  const handleAprovar = async () => {
    if (decryptedId) {
      await aprovarSolicitacao(decryptedId);
      toast({
        title: "Status atualizado",
        description: "Solicitação aprovada com sucesso.",
      });
      await refreshSolicitacoes();
      navigate("/solicitacoes", { state: { shouldRefresh: true } });
    }
  };


  const handleMudarStatus = async () => {
    if (decryptedId && novoStatus) {
      await atualizarStatus(decryptedId, novoStatus);
      await refreshSolicitacoes();
      setShowStatusDialog(false);
      setNovoStatus("");
      toast({
        title: "Status atualizado",
        description: "Status alterado com sucesso.",
      });
      navigate("/solicitacoes", { state: { shouldRefresh: true } });
    }
  };

  const handleEnviarComentario = async () => {
    if (decryptedId && novoComentario.trim()) {
      await adicionarComentario(decryptedId, novoComentario.trim());
      setNovoComentario("");
    }
  };

  const handleEnviarObservacao = async () => {
    if (decryptedId && observacaoInterna.trim()) {
      await adicionarObservacaoInterna(decryptedId, observacaoInterna.trim());
      setObservacaoInterna("");
    }
  };

  const handleAtribuirAgente = async () => {
    if (decryptedId && agenteSelecionado) {
      await atribuirAgente(decryptedId, agenteSelecionado);
      setShowAtribuirAgenteDialog(false);
      setAgenteSelecionado("");
      toast({
        title: "Agente atribuído",
        description: "Agente atribuído com sucesso.",
      });
      await refreshSolicitacoes();
    }
  };

  const handleEncaminharOuvidoria = async () => {
    if (decryptedId && ouvidoriaSelecionada) {
      await transferirSolicitacao(decryptedId, ouvidoriaSelecionada);
      setShowEncaminharDialog(false);
      setOuvidoriaSelecionada("");
      toast({
        title: "Solicitação encaminhada",
        description: "Solicitação encaminhada para outra ouvidoria com sucesso.",
      });
      await refreshSolicitacoes();
      navigate("/solicitacoes", { state: { shouldRefresh: true } });
    }
  };

  // Filter ouvidorias by topic etiquetas - only show ouvidorias with matching categories
  const getOuvidoriasFiltradas = () => {
    const topicEtiquetas = solicitacaoAtual?.categoria?.etiquetas;
    
    // Se o tópico não tem etiquetas, retorna todas as ouvidorias
    if (!topicEtiquetas || topicEtiquetas.length === 0) {
      return ouvidoriasSupervisionadas;
    }
    
    // Filtra ouvidorias que têm pelo menos uma etiqueta em comum com o tópico
    return ouvidoriasSupervisionadas.filter(ouv => {
      const ouvidoriaEtiquetas = ouv.etiquetas;
      if (!ouvidoriaEtiquetas || ouvidoriaEtiquetas.length === 0) {
        return false;
      }
      return ouvidoriaEtiquetas.some(etiqueta => topicEtiquetas.includes(etiqueta));
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">
          Carregando solicitação...
        </span>
      </div>
    );
  }

  if (!solicitacaoAtual) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <NotFoundState
          resourceName="solicitação"
          onGoBack={() => navigate("/solicitacoes")}
        />
      </div>
    );
  }

  const s = solicitacaoAtual;
  const slaStatus = s.sla_vencimento
    ? calcularStatusSLA(s.sla_vencimento)
    : "normal";

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center justify-between gap-4"
      >
        <div className="flex items-start gap-2 sm:gap-4 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/solicitacoes")}
            className="flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-urbanist break-words">
                {s.protocolo || "Sem protocolo"}
              </h1>
              <SolicitacaoStatusBadge
                status={s.status}
                estadoAprovacao={s.estadoAprovacao}
              />
            </div>
            <p className="text-sm sm:text-base lg:text-lg text-muted-foreground mt-1 break-words">
              {s.titulo || s.descricao || "Sem título"}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {!isReadOnly || s.estadoAprovacao === 'Aguardando aprovação' ? (
            <>
              {/* Botão Aprovar */}
              <Button
                onClick={handleAprovar}
                disabled={isSaving}
                className="gap-2"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Aprovar
              </Button>

              {/* Botão Reprovar */}
              <Button
                variant="destructive"
                disabled={isSaving}
                className="gap-2"
                onClick={async () => {
                  if (decryptedId) {
                    await reprovarSolicitacao(decryptedId, "");
                    toast({
                      title: "Status atualizado",
                      description: "Solicitação reprovada com sucesso.",
                    });
                    await refreshSolicitacoes();
                    navigate("/solicitacoes", { state: { shouldRefresh: true } });
                  }
                }}
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                Reprovar
              </Button>
            </>
          ) : null}

          {!isReadOnly && (
          <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2">
                <History className="h-4 w-4" />
                Alterar Status
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Alterar Status</DialogTitle>
                <DialogDescription>
                  Selecione o novo status para esta solicitação.
                </DialogDescription>
              </DialogHeader>
              <Select
                value={novoStatus}
                onValueChange={(v) => setNovoStatus(v as StatusSolicitacao)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS)
                    .filter(([value]) => value !== "aprovada" && value !== "reprovada")
                    .map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowStatusDialog(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleMudarStatus}
                  disabled={!novoStatus || isSaving}
                >
                  Confirmar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}

          {!isReadOnly && (
            <Dialog open={showAtribuirAgenteDialog} onOpenChange={setShowAtribuirAgenteDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <UserPlus className="h-4 w-4" />
                  Atribuir Agente
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Atribuir Agente</DialogTitle>
                  <DialogDescription>
                    Selecione o agente responsável por esta solicitação.
                  </DialogDescription>
                </DialogHeader>
                <Select
                  value={agenteSelecionado}
                  onValueChange={setAgenteSelecionado}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o agente" />
                  </SelectTrigger>
                  <SelectContent>
                    {agentes.map((agente) => (
                      <SelectItem key={agente.id} value={agente.id}>
                        {agente.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowAtribuirAgenteDialog(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleAtribuirAgente}
                    disabled={!agenteSelecionado || isSaving}
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Atribuir
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {!isReadOnly && (
            <Dialog open={showEncaminharDialog} onOpenChange={setShowEncaminharDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Building2 className="h-4 w-4" />
                  Encaminhar para outra ouvidoria
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Encaminhar para outra ouvidoria</DialogTitle>
                  <DialogDescription>
                    Selecione a ouvidoria para encaminhar esta solicitação. Apenas ouvidorias com categorias compatíveis serão exibidas.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4 max-h-96 overflow-y-auto">
                  {getOuvidoriasFiltradas().length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">
                      Nenhuma ouvidoria encontrada com categorias compatíveis.
                    </p>
                  ) : (
                    getOuvidoriasFiltradas().map((ouvidoria) => (
                      <div
                        key={ouvidoria.id}
                        onClick={() => setOuvidoriaSelecionada(ouvidoria.id)}
                        className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          ouvidoriaSelecionada === ouvidoria.id
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50 hover:bg-muted/50"
                        }`}
                      >
                        {ouvidoria.logo?.arquivo ? (
                          <img
                            src={resolveMediaUrl(ouvidoria.logo.arquivo) || ouvidoria.logo.arquivo}
                            alt={ouvidoria.nomeFantasia || ouvidoria.nome}
                            className="h-16 w-16 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Building2 className="h-8 w-8 text-primary" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-semibold text-base">
                            {ouvidoria.nomeFantasia || ouvidoria.nome}
                          </p>
                          {ouvidoria.etiquetas && ouvidoria.etiquetas.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {ouvidoria.etiquetas.map((etiqueta) => (
                                <Badge key={etiqueta} variant="secondary" className="text-xs">
                                  {etiqueta}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        {ouvidoriaSelecionada === ouvidoria.id && (
                          <CheckCircle className="h-6 w-6 text-primary" />
                        )}
                      </div>
                    ))
                  )}
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowEncaminharDialog(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleEncaminharOuvidoria}
                    disabled={!ouvidoriaSelecionada || isSaving}
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Encaminhar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2 space-y-6"
        >
          {/* Identificação */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Identificação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-base">
                <div>
                  <p className="text-sm text-muted-foreground">Protocolo</p>
                  <p className="font-mono font-semibold text-sm">
                    {s.protocolo || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tópico</p>
                  <p className="font-semibold">
                    {s.categoria_texto || s.titulo || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className="font-semibold">
                    {s.status ? STATUS_LABELS[s.status] || s.status : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Data de Criação
                  </p>
                  <p className="font-semibold">{formatDate(s.created_at)}</p>
                </div>
                {s.status === 'resolvida' && (
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Data de Conclusão
                    </p>
                    <p className="font-semibold">{formatDate(s.data_conclusao)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Localização e Imagens */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Localização
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium">
                    {s.localizacao_endereco || "Nenhum endereço"}
                  </p>
                  {s.localizacao_lat !== null && s.localizacao_lng !== null && (
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span>
                        Lat: {s.localizacao_lat?.toFixed(6)}, Lng:{" "}
                        {s.localizacao_lng?.toFixed(6)}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-3 bg-gradient-to-r from-primary/90 via-secondary/90 to-primary/90 text-white border-none shadow-sm hover:shadow-md hover:brightness-110 transition"
                        onClick={(e) => {
                          e.stopPropagation();
                          const lat = s.localizacao_lat;
                          const lng = s.localizacao_lng;
                          if (lat !== null && lng !== null) {
                            window.open(
                              `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=18/${lat}/${lng}`,
                              "_blank",
                            );
                          }
                        }}
                      >
                        Ver no mapa
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {s.data_ocorrencia && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>
                    Data da ocorrência: {formatDate(s.data_ocorrencia)}
                  </span>
                </div>
              )}

              {/* Imagens */}
              {s.anexos && s.anexos.length > 0 ? (
                <div>
                  <p className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
                    <FileImage className="h-4 w-4" />
                    {s.anexos.length} anexo(s)
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {s.anexos.map((anexo, index) => (
                      <a
                        key={anexo.id || index}
                        href={resolveMediaUrl(anexo.url) || anexo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative aspect-video overflow-hidden rounded-lg border bg-muted hover:ring-2 hover:ring-primary transition-all"
                      >
                        <AnexoThumbnail
                          url={anexo.url}
                          alt={`Anexo ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhum anexo</p>
              )}
            </CardContent>
          </Card>

          

          {/* Tabs: Histórico, Comentários, Observações */}
          {/* <Card>
            <Tabs defaultValue="historico" className="w-full">
              <CardHeader>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="historico" className="gap-2">
                    <History className="h-4 w-4" />
                    Histórico
                  </TabsTrigger>
                  <TabsTrigger value="comentarios" className="gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Comentários
                  </TabsTrigger>
                  <TabsTrigger value="observacoes" className="gap-2">
                    <FileText className="h-4 w-4" />
                    Observações
                  </TabsTrigger>
                </TabsList>
              </CardHeader>
              <CardContent>
                <TabsContent value="historico" className="mt-0">
                  <div className="space-y-3">
                    {s.historico && s.historico.length > 0 ? (
                      s.historico.map((h) => (
                        <div
                          key={h.id}
                          className="flex gap-3 p-3 bg-muted/50 rounded-lg"
                        >
                          <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {h.status_anterior && (
                                <>
                                  <SolicitacaoStatusBadge
                                    status={h.status_anterior}
                                  />
                                  <span className="text-muted-foreground">
                                    →
                                  </span>
                                </>
                              )}
                              <SolicitacaoStatusBadge status={h.status_novo} />
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {h.agente_nome || "Sistema"} •{" "}
                              {formatDate(h.created_at)}
                            </p>
                            {h.observacao && (
                              <p className="text-sm mt-1">{h.observacao}</p>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted-foreground text-center py-4">
                        Nenhum histórico registrado
                      </p>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="comentarios" className="mt-0 space-y-4">
                  {!isReadOnly && (
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Adicionar comentário..."
                        value={novoComentario}
                        onChange={(e) => setNovoComentario(e.target.value)}
                        rows={2}
                      />
                      <Button
                        onClick={handleEnviarComentario}
                        disabled={!novoComentario.trim() || isSaving}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <Separator />
                  <div className="space-y-3">
                    {s.comentarios && s.comentarios.length > 0 ? (
                      s.comentarios.map((c) => (
                        <div key={c.id} className="p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium text-sm">
                              {c.autor_nome}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(c.created_at)}
                            </span>
                            {!c.visivel_cidadao && (
                              <Badge variant="outline" className="text-xs">
                                Interno
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm mt-2">{c.conteudo}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted-foreground text-center py-4">
                        Nenhum comentário
                      </p>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="observacoes" className="mt-0 space-y-4">
                  {!isReadOnly && (
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Adicionar observação interna (não visível ao cidadão)..."
                        value={observacaoInterna}
                        onChange={(e) => setObservacaoInterna(e.target.value)}
                        rows={2}
                      />
                      <Button
                        onClick={handleEnviarObservacao}
                        disabled={!observacaoInterna.trim() || isSaving}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <Separator />
                  {s.observacoes_internas ? (
                    <div className="p-3 bg-muted/50 rounded-lg whitespace-pre-wrap text-sm">
                      {s.observacoes_internas}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">
                      Nenhuma observação interna
                    </p>
                  )}
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card> */}
        </motion.div>

        {/* Right Column - Sidebar */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          {/* Validação */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Validação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Estado de Aprovação:
                  </span>
                  <Badge
                    variant={
                      s.resultado_validacao === "aprovada"
                        ? "default"
                        : s.resultado_validacao === "reprovada"
                          ? "destructive"
                          : "secondary"
                    }
                  >
                    {s.resultado_validacao
                      ? s.resultado_validacao.charAt(0).toUpperCase() +
                        s.resultado_validacao.slice(1)
                      : "Aguardando"}
                  </Badge>
                </div>
                {s.tipo_validacao && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Tipo de Validação:
                    </span>
                    <span className="text-sm font-medium">
                      {s.tipo_validacao}
                    </span>
                  </div>
                )}
              </div>

              {s.justificativa_reprovacao && (
                <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                  <p className="text-sm font-medium text-destructive">
                    Justificativa da Reprovação
                  </p>
                  <p className="text-sm mt-1">{s.justificativa_reprovacao}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Métricas da Solicitação (sem visualizações) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Engajamento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <GradientFavoriteIcon className="h-6 w-6 drop-shadow-sm" />
                <div>
                  <p className="text-2xl font-bold leading-tight">
                    {s.apoios || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Apoios</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Agentes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Agente Responsável
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Agente Responsável Principal (se vier da API) */}
              {s.agenteResponsavel && (
                <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-lg">
                  <AgentAvatar
                    src={s.agenteResponsavel.fotoPerfil?.arquivo || ""}
                    alt={s.agenteResponsavel.nome}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold">
                      {s.agenteResponsavel.nome || "Agente Responsável"}
                    </p>
                    {s.agenteResponsavel.email && (
                      <p className="text-xs text-muted-foreground">
                        {s.agenteResponsavel.email}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Lista de agentes atribuídos */}
              {s.agentes && s.agentes.length > 0 ? (
                <div className="space-y-2">
                  {s.agentes.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg"
                    >
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {a.profile?.nome || "Agente"}
                        </p>
                        {a.profile?.cargo && (
                          <p className="text-xs text-muted-foreground">
                            {a.profile.cargo}
                          </p>
                        )}
                      </div>
                      {a.principal && (
                        <Badge variant="default" className="text-xs">
                          Principal
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : !s.agenteResponsavel && (
                <p className="text-muted-foreground text-sm text-center py-2">
                  Nenhum agente atribuído
                </p>
              )}
              {!isReadOnly && (
                <Button 
                  variant="outline" 
                  className="w-full mt-3 gap-2" 
                  size="sm"
                  onClick={() => setShowAtribuirAgenteDialog(true)}
                >
                  <UserPlus className="h-4 w-4" />
                  Trocar agente responsável
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Ações Administrativas */}
          {!isReadOnly && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Ações Disponíveis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                  size="sm"
                >
                  <Send className="h-4 w-4" />
                  Encaminhar para outra ouvidoria
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                  size="sm"
                >
                  <History className="h-4 w-4" />
                  Reatribuir responsável
                </Button>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </div>
    </div>
  );
}
